import { debounce } from 'lodash-es';
import { useCallback, useEffect } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useTranslation } from '@klv/hooks/useTranslation';
import { generateClassName } from '@klv/shared/utils/functions';
import { updateSearchQueryParam } from '@klv/organisms/unified-search/utils';

import Icon from '@klv/atoms/icon';

import * as s from './styles/unified-search-input.module.css';

import type { IUnifiedSearchInputProps } from './types';

const UnifiedSearchInput = ({ backgroundColor = 'light', searchBox, placeholder, ...rest }: IUnifiedSearchInputProps) => {
  const { t } = useTranslation();

  // Initialize Algolia SearchBox.
  const { query, refine } = useSearchBox();

  const urlSearchParams = new URLSearchParams(location?.search);
  const urlQuery = urlSearchParams.get('query');

  const doSearch = useCallback((searchFieldValue: string) => {
    updateSearchQueryParam(searchFieldValue);
    refine(searchFieldValue);
  }, [refine]);

  const clearInput = () => {
    if (searchBox?.current) searchBox.current.value = '';
    doSearch('');
  };

  useEffect(() => {
    if (urlQuery) {
      refine(urlQuery);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = debounce((evt: React.ChangeEvent<HTMLInputElement>) => {
    doSearch(evt.target.value);
  }, 250);
  
  return (
    <div className={generateClassName([s.unifiedSearch, s[backgroundColor]])}>
      <Icon className={s.searchIcon} color='currentColor' iconName='Search' />
      <input 
        ref={searchBox}
        autoCapitalize='none'
        autoComplete='off'
        autoCorrect='off'
        defaultValue={urlQuery || query}
        id='search'
        name='query'
        placeholder={placeholder ? placeholder : t('pages_search_placeholder')}
        spellCheck='false'
        type='text'
        onChange={handleChange}
        className={`${s.input}${rest.className ? rest.className : ''}`}
      />
      <button type="button" className={s.cancelButton} onClick={clearInput}>
        <Icon className={s.cancelIcon} color='currentColor' iconName='X' />
      </button>
    </div>
  );
};

export default UnifiedSearchInput;
